<template>
  <main>
    <form class="container" @submit.prevent="save">
      <div class="row">
        <div class="col-full heading-xl">
          <h1 class="margin-bottom-md">
            {{ $t('contactInformation.heading') }}
          </h1>
          <p class="heading-md margin-bottom-xs">
            {{ $t('common.userId') }}:
            <span data-test="user-id-string" class="p">{{ email }}</span>
          </p>
          <p class="heading-md margin-bottom-none">
            {{ $t('common.mobileNumber') }}:
            <span data-test="mobile-number-string" class="p">{{ mobileNumber }}</span>
          </p>
        </div>
      </div>
      <div class="row">
        <div class="col-full">
          <hr class="margin-bottom-md margin-top-none" aria-hidden="true" />
        </div>
      </div>
      <div class="row">
        <div class="col-full">
          <h2 class="heading-md weight-bold">
            {{ $t('contactInformation.updateNumber') }}
          </h2>
          <p class="legal">{{ $t('contactInformation.legalNotice') }}</p>
          <al-contact-input
            v-model="newMobile"
            :countries="countries"
            :label="$t('contactInformation.newNumber')"
            name="phoneNumber"
            @input="handleInput">
            <span v-if="showManualError" slot="manualError">
              <i18n :path="$route.params.manualError">
                <a :href="customerServiceContact.href" place="customerServiceNumber">{{
                  customerServiceContact.val
                }}</a>
              </i18n>
            </span>
          </al-contact-input>
          <al-contact-input
            :key="confirmNewMobileKey"
            v-model="confirmNewMobile"
            :disabled="isConfirmMobileDisabled"
            :countries="countries"
            :label="$t('contactInformation.confirmMobile')"
            :match="newMobile"
            name="confirmPhoneNumber" />
          <p class="legal">{{ $t('provisionCreate.mobileHelperText') }}</p>
        </div>
      </div>

      <al-notify v-if="errorToken || manualPageError" error name="error-msg">
        {{ $t(errorToken || manualPageError) }}
      </al-notify>

      <div class="row row-vertical-align-bottom">
        <div class="col-full align-center">
          <al-button
            v-analytics:click="$EVENTS.SAVE_BUTTON"
            uppercase
            :disabled="!formIsValid"
            :loading="loading"
            :label="$t('button.next')"
            name="form-submit-button" />
        </div>
        <div class="col-full align-center">
          <back-button v-analytics:click="$EVENTS.GO_BACK_BUTTON" :location="backButtonRoute">
            {{ $t('button.goBack') }}
          </back-button>
        </div>
      </div>
    </form>
  </main>
</template>

<script>
import { AlButton, AlContactInput, AlNotify } from '@mc-alberta/alcapone'
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import BackButton from '@/components/BackButton'
import useConfirmModal from '@/composables/useConfirmModal'
import useFormValidation from '@/composables/useFormValidation'
import { NETWORK, ROUTES } from '@/constants'
import * as types from '@/store/mutation-types'

export default {
  name: ROUTES.CONTACT_INFORMATION_SETTINGS,
  components: { AlButton, AlContactInput, AlNotify, BackButton },
  beforeRouteLeave(to, from, next) {
    this.beforeRouteLeaveConfirmModal(to, from, next)
  },
  setup(props, context) {
    const { beforeRouteLeaveConfirmModal } = useConfirmModal({ context })
    const { hasValidationErrors } = useFormValidation()

    return {
      beforeRouteLeaveConfirmModal,
      hasValidationErrors
    }
  },
  data() {
    const { countryCode } = this.$store.state.user.consumer
    return {
      backButtonRoute: { name: ROUTES.SETTINGS },
      countries: this.$i18nData.getPhonesConfig(),
      newMobile: { country: countryCode },
      confirmNewMobile: { country: countryCode },
      errorToken: null,
      confirmNewMobileKey: 0
    }
  },
  computed: {
    ...mapState({
      serviceLayerError: (state) => state.serviceLayerError,
      currentCountry: (state) => state.user.consumer.countryCode,
      loading: (state) => state.actionIsPending,
      contactInfo: (state) => state.identity.verificationData.contactInfo
    }),
    isConfirmMobileDisabled() {
      return this.fields?.phoneNumber?.invalid || this.showManualError
    },
    formIsValid() {
      let isValid = false

      if (!this.isConfirmMobileDisabled && this.fields?.confirmPhoneNumber?.changed) {
        isValid = !this.hasValidationErrors
      }

      return isValid
    },
    ...mapGetters([
      'formattedUserEmail',
      'formattedUserMobileNumber',
      'oldestActiveCard',
      'serviceErrorResponse'
    ]),
    email() {
      return this.formattedUserEmail
    },
    mobileNumber() {
      return this.formattedUserMobileNumber(this)
    },
    customerServiceContact() {
      const countryConfig = this.$i18nData.getCountryConfig(this.currentCountry)
      const phoneNumber = countryConfig?.customerSupportPhoneNumber || ''
      const email = countryConfig?.customerSupportEmail || ''

      if (email) {
        return {
          href: `mailto:${email}`,
          val: email
        }
      } else {
        return {
          href: `tel:${phoneNumber}`,
          val: phoneNumber
        }
      }
    },
    manualPageError() {
      return this.$route.params?.errorToken
    },
    hasManualError() {
      return !!this.$route.params?.manualError
    },
    isNewNumber() {
      return this.newMobile.phoneNumber !== this.contactInfo.mobileNumber
    },
    showManualError() {
      return this.hasManualError && !this.isNewNumber
    }
  },
  created() {
    if (this.hasManualError) {
      this.newMobile.phoneNumber = this.contactInfo.mobileNumber
    }
  },
  mounted() {
    this.$validator.validateAll()
  },
  methods: {
    ...mapActions(['clearServiceLayerError', 'verifyChannel']),
    ...mapMutations({
      setActionIsPending: types.SET_ACTION_IS_PENDING,
      setInterceptUnauthorized: types.SET_INTERCEPT_UNAUTHORIZED_ERROR
    }),
    handleInput() {
      if (this.fields?.confirmPhoneNumber?.dirty) {
        const { countryCode } = this.$store.state.user.consumer
        this.confirmNewMobile = { country: countryCode }
        this.confirmNewMobileKey++
      }
    },
    async save() {
      this.clearServiceLayerError()
      this.setInterceptUnauthorized(false)
      this.setActionIsPending(true)

      const { countryCode, phoneNumber } = this.newMobile
      await this.verifyChannel({
        phone: {
          countryCode,
          mobileNumber: phoneNumber
        }
      })

      if (!this.serviceLayerError) {
        this.$router.push({
          name: ROUTES.VERIFY_MOBILE,
          params: { skipConfirmation: true }
        })
      } else {
        this.handleError()
      }
    },
    handleError() {
      const { status, data } = this.serviceErrorResponse() || {}

      if (status === 401 && data.status === 'CVC_VERIFICATION_REQUIRED') {
        this.goToNextRoute()
      } else {
        this.errorToken = 'error.default'
        this.setActionIsPending(false)
      }
    },
    goToNextRoute() {
      const oldestCard = this.oldestActiveCard
      const { countryCode, phoneNumber } = this.newMobile

      if (oldestCard.paymentNetworkName.toLowerCase() === NETWORK.MAESTRO) {
        this.$router.push({
          name: ROUTES.CONTACT_INFORMATION_SUPPORT,
          params: { skipConfirmation: true }
        })
      } else {
        this.$router.push({
          name: ROUTES.CVC,
          params: {
            oldestCard,
            mobileNumber: {
              countryCode,
              mobileNumber: phoneNumber
            },
            showStepTracker: true,
            totalStep: 2
          }
        })
      }
    }
  }
}
</script>
